/**
 * Playback — turning a finished round's export back into a live game.
 *
 * The evening is recorded twice over (decision E3): `keystrokes.json` holds
 * every stroke with its wall time, caret position and both clocks;
 * `story.json` adds the moderator's layer — jokers, penalties, pauses, the
 * event log. This module folds either (or both) back into the very same
 * `PublicState` the WebSocket hub broadcasts during the show, so the
 * projection can be replayed on the beamer with the identical interface
 * rather than a separate "player" that only looks similar.
 *
 * Two pieces:
 *   · `parseRecording()` — validate + normalise the export files.
 *   · `stateAt()`        — the state at any point of the timeline, O(log n).
 *
 * Everything here is pure: no DOM, no clock, no I/O. The transport (play,
 * pause, seek, speed) lives in the client and only moves the playhead.
 *
 * Reconstruction rules, in the same order the engine applies them:
 *   · a `char` stroke appends its character and *is* proof that its author
 *     was on the clock — the server only ever accepts one from the seat at
 *     the keyboard, so the stroke carries the turn;
 *   · a `turn_pass` stroke hands over to the other seat with time left;
 *   · `clocksMs` on every stroke is authoritative — between two strokes the
 *     running side is interpolated, which is exact because `elapsedMs` and
 *     the clocks share one wall-time base (F-35);
 *   · events (story.json) add what leaves no keystroke: pauses, jokers,
 *     penalties, timeouts, completion, the end of the round.
 *
 * A keystroke-only recording therefore already replays text, turns and both
 * clocks; story.json fills in the margin annotations.
 */

import { DEFAULT_DISPLAY } from './fonts';
import {
  AUTHOR_COLORS,
  COMIC_PANELS,
  DEFAULT_CONFIG,
  MAX_AUTHORS,
  countWords,
  type ActiveJoker,
  type AuthorSeat,
  type ClockMark,
  type ComicDef,
  type DisplaySettings,
  type EndReason,
  type GameEvent,
  type JokerTarget,
  type Keystroke,
  type PublicState,
  type RoundKind,
  type RoundPhase,
  type Segment,
  type SessionConfig,
} from './types';

/**
 * The task slide runs ahead of the replayed round, the way it does on the
 * evening itself: the audience reads the Schreibimpuls, then the clocks
 * start. Six seconds — long enough for a sentence on a beamer.
 */
export const PREROLL_MS = 6_000;

export type PlaybackSpeed = 0.5 | 1 | 2 | 4 | 8;
export const PLAYBACK_SPEEDS: PlaybackSpeed[] = [0.5, 1, 2, 4, 8];

/** A point worth jumping to on the scrubber (and worth a tick mark). */
export interface PlaybackMarker {
  /** Position on the playback timeline, pre-roll included. */
  atMs: number;
  kind: 'start' | 'pass' | 'joker' | 'jokerEnd' | 'penalty' | 'bonus' | 'pause' | 'frozen' | 'end';
  label: string;
}

export interface PlaybackRecording {
  /** Which files went into it — shown in the loader. */
  sources: string[];
  hasStoryLayer: boolean;
  schemaVersion: string;
  pin: string;
  sessionCreatedAt: number;
  roundNumber: number;
  kind: RoundKind;
  comicId: string | null;
  comics: ComicDef[];
  prompt: string;
  config: SessionConfig;
  display: DisplaySettings;
  authors: AuthorSeat[];
  /** Epoch ms of the round start — the base of every `elapsedMs`. */
  startedAtEpoch: number;
  /** Length of the round itself, without the pre-roll. */
  roundMs: number;
  /** Length of the whole playback: pre-roll + round. */
  totalMs: number;
  endReason: EndReason | null;
  fullText: string;
  keystrokeCount: number;
  /** Strokes the no-delete rule swallowed — Backspace, paste, out-of-turn. */
  blockedCount: number;
  charCount: number;
  wordCount: number;
  markers: PlaybackMarker[];
  /** Discrete state changes, sorted by `atMs` (round time). */
  frames: Frame[];
}

/**
 * One discrete state change. Everything that moves continuously between two
 * frames (the running clock, a joker countdown) is interpolated in
 * `stateAt`; arrays are shared between frames until something mutates them,
 * so a 2 000-stroke round stays a handful of allocations per annotation.
 */
interface Frame {
  atMs: number;
  textLen: number;
  activeAuthor: number;
  remainingMs: number[];
  /** Seat whose clock counts down from here on; null while nothing runs. */
  ticking: number | null;
  frozen: boolean[];
  confirmedDone: boolean[];
  phase: RoundPhase;
  panelStarts: number[];
  activeJokers: ActiveJoker[];
  endedJokers: ActiveJoker[];
  marks: ClockMark[];
  segments: Segment[];
  endReason: EndReason | null;
}

/* ======================================================== file parsing == */

export class RecordingError extends Error {}

interface RawFile {
  name: string;
  data: unknown;
}

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

function epoch(v: unknown): number | null {
  if (typeof v !== 'string') return null;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? ms : null;
}

/** Recognise an export file by its head, whatever the filename says. */
export function exportKind(data: unknown): 'story' | 'keystrokes' | null {
  if (!isObj(data)) return null;
  if (data['schema'] !== 'schreibarena-export') return null;
  if (Array.isArray(data['timeline'])) return 'keystrokes';
  if (isObj(data['story'])) return 'story';
  return null;
}

/**
 * Fold one or two export files into a recording. Order does not matter, and
 * either file alone is enough: keystrokes.json replays the typing and the
 * clocks, story.json alone still replays jokers, penalties and the finished
 * text (without the character-by-character stream).
 */
export function parseRecording(files: RawFile[]): PlaybackRecording {
  let keys: Obj | undefined;
  let story: Obj | undefined;
  const sources: string[] = [];

  for (const f of files) {
    const kind = exportKind(f.data);
    if (kind === null) {
      throw new RecordingError(
        `„${f.name}“ ist kein Schreibarena-Export. Erwartet werden die Dateien aus dem Runden-Zip.`,
      );
    }
    if (kind === 'keystrokes') keys = f.data as Obj;
    else story = f.data as Obj;
    sources.push(f.name);
  }

  const head = (keys ?? story) as Obj | undefined;
  if (!head) throw new RecordingError('Keine Datei geladen.');

  if (keys && story && !sameRound(keys, story)) {
    throw new RecordingError('Die beiden Dateien gehören zu verschiedenen Runden.');
  }

  const roundMeta = isObj(head['round']) ? head['round'] : {};
  const sessionMeta = isObj(head['session']) ? head['session'] : {};

  const startedAtEpoch = epoch(roundMeta['startedAt']);
  if (startedAtEpoch === null) {
    throw new RecordingError('Der Export hat keinen Rundenstart — nur beendete Runden lassen sich abspielen.');
  }
  const endedAtEpoch = epoch(roundMeta['endedAt']);

  const config: SessionConfig = {
    ...DEFAULT_CONFIG,
    ...(isObj(roundMeta['config']) ? (roundMeta['config'] as Partial<SessionConfig>) : {}),
  };

  const authors = parseAuthors(head['authors']);
  const timeline = keys ? parseTimeline(keys['timeline'], authors.length) : [];
  const storySegments = story ? parseStorySegments(story['story']) : [];
  const events = story ? parseEvents(story['events']) : [];
  const marks = story ? parseMarks(story['clockMarks'], startedAtEpoch) : [];
  const jokerDefs = story ? parseJokerDescriptions(story['jokers']) : new Map<string, string>();

  const fullText = timeline.length > 0 ? timeline.filter((k) => k.kind === 'char').map((k) => k.char ?? '').join('') : storyText(story);

  // schemaVersion ≥ 1.2.0 carries the round format and the comic library, so
  // a Bildrunde replays on the comic board. Older exports are story rounds.
  const kind: RoundKind = roundMeta['kind'] === 'comic' ? 'comic' : 'story';
  const comics = parseComics(sessionMeta['comics']);
  const comicId = typeof roundMeta['comicId'] === 'string' ? roundMeta['comicId'] : null;
  const display: DisplaySettings = isObj(sessionMeta['display'])
    ? { ...DEFAULT_DISPLAY, ...(sessionMeta['display'] as Partial<DisplaySettings>) }
    : { ...DEFAULT_DISPLAY };

  const lastStroke = timeline[timeline.length - 1]?.elapsedMs ?? 0;
  const lastEvent = events[events.length - 1]?.elapsedMs ?? 0;
  const roundMs = Math.max(
    endedAtEpoch === null ? 0 : endedAtEpoch - startedAtEpoch,
    lastStroke,
    lastEvent,
    1,
  );

  const built = buildFrames({
    timeline,
    events,
    marks,
    storySegments,
    panelStarts: Array.isArray(roundMeta['panelStarts']) ? (roundMeta['panelStarts'] as number[]).map((v) => num(v)) : [0],
    jokerDefs,
    authors,
    config,
    kind,
    fullText,
    roundMs,
    startedAtEpoch,
    endReason: parseEndReason(roundMeta['endReason']),
  });

  return {
    sources,
    hasStoryLayer: story !== undefined,
    schemaVersion: str(head['schemaVersion'], '1.0.0'),
    pin: str(sessionMeta['pin'], '000000'),
    sessionCreatedAt: epoch(sessionMeta['createdAt']) ?? startedAtEpoch,
    roundNumber: num(roundMeta['number'], 1),
    kind,
    comicId,
    comics,
    prompt: str(roundMeta['prompt']),
    config,
    display,
    authors,
    startedAtEpoch,
    roundMs,
    totalMs: PREROLL_MS + roundMs,
    endReason: parseEndReason(roundMeta['endReason']),
    fullText,
    keystrokeCount: timeline.length,
    blockedCount: timeline.filter((k) => k.kind === 'blocked').length,
    charCount: fullText.length,
    wordCount: countWords(fullText),
    markers: built.markers,
    frames: built.frames,
  };
}

function sameRound(a: Obj, b: Obj): boolean {
  const ra = isObj(a['round']) ? a['round'] : {};
  const rb = isObj(b['round']) ? b['round'] : {};
  return num(ra['number'], -1) === num(rb['number'], -2) && str(ra['startedAt']) === str(rb['startedAt']);
}

function storyText(story: Obj | undefined): string {
  if (!story) return '';
  const s = isObj(story['story']) ? story['story'] : {};
  return str(s['finalText']);
}

function parseAuthors(raw: unknown): AuthorSeat[] {
  const list = Array.isArray(raw) ? raw : [];
  const seats: AuthorSeat[] = list.filter(isObj).map((a, i) => ({
    index: num(a['index'], i),
    name: str(a['name'], `Autor:in ${i + 1}`),
    color: str(a['color'], AUTHOR_COLORS[i] ?? '#fff7ec'),
    connected: true,
  }));
  while (seats.length < MAX_AUTHORS) {
    const i = seats.length;
    seats.push({ index: i, name: `Autor:in ${i + 1}`, color: AUTHOR_COLORS[i] ?? '#fff7ec', connected: true });
  }
  return seats;
}

function parseComics(raw: unknown): ComicDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isObj).map((c) => ({
    id: str(c['id']),
    title: str(c['title'], 'Comic'),
    cols: num(c['cols'], 2),
    rows: num(c['rows'], 3),
    width: num(c['width'], 1000),
    height: num(c['height'], 1500),
    mime: str(c['mime'], 'image/jpeg'),
    uploadedAt: num(c['uploadedAt'], 0),
  }));
}

function parseEndReason(raw: unknown): EndReason | null {
  return raw === 'time' || raw === 'completed' || raw === 'forced' ? raw : null;
}

function parseTimeline(raw: unknown, seats: number): Keystroke[] {
  if (!Array.isArray(raw)) throw new RecordingError('Der Tastatur-Zeitstrahl fehlt oder ist beschädigt.');
  const out: Keystroke[] = [];
  for (const k of raw) {
    if (!isObj(k)) continue;
    const kind = k['kind'];
    out.push({
      seq: num(k['seq'], out.length + 1),
      t: str(k['t']),
      elapsedMs: Math.max(0, num(k['elapsedMs'])),
      author: Math.min(Math.max(0, num(k['author'])), seats - 1),
      key: str(k['key']),
      char: typeof k['char'] === 'string' ? k['char'] : null,
      pos: num(k['pos']),
      clocksMs: Array.isArray(k['clocksMs']) ? k['clocksMs'].map((v) => num(v)) : [],
      kind: kind === 'turn_pass' || kind === 'blocked' ? kind : 'char',
    });
  }
  out.sort((a, b) => a.elapsedMs - b.elapsedMs || a.seq - b.seq);
  return out;
}

function parseEvents(raw: unknown): GameEvent[] {
  if (!Array.isArray(raw)) return [];
  const out: GameEvent[] = [];
  for (const e of raw) {
    if (!isObj(e) || typeof e['type'] !== 'string') continue;
    out.push({
      seq: num(e['seq']),
      t: str(e['t']),
      elapsedMs: Math.max(0, num(e['elapsedMs'])),
      type: e['type'] as GameEvent['type'],
      data: isObj(e['data']) ? e['data'] : {},
    });
  }
  out.sort((a, b) => a.elapsedMs - b.elapsedMs || a.seq - b.seq);
  return out;
}

function parseMarks(raw: unknown, startedAtEpoch: number): (ClockMark & { elapsedMs: number })[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isObj).map((m) => {
    const at = epoch(m['t']);
    return {
      textPos: num(m['textPos']),
      author: num(m['author']),
      deltaMs: num(m['deltaMs']),
      reason: typeof m['reason'] === 'string' ? m['reason'] : null,
      t: str(m['t']),
      elapsedMs: at === null ? 0 : Math.max(0, at - startedAtEpoch),
    };
  });
}

/**
 * The author segments as story.json records them. Only needed when there is
 * no keystroke timeline to derive them from — see `buildFrames`.
 */
function parseStorySegments(raw: unknown): Segment[] {
  const story = isObj(raw) ? raw : {};
  const list = Array.isArray(story['segments']) ? story['segments'] : [];
  return list.filter(isObj).map((seg) => ({ author: num(seg['author']), start: num(seg['start']) }));
}

/** The joker descriptions live in story.json's `jokers`, not in the events. */
function parseJokerDescriptions(raw: unknown): Map<string, string> {
  const out = new Map<string, string>();
  if (!Array.isArray(raw)) return out;
  for (const j of raw) {
    if (isObj(j) && typeof j['instanceId'] === 'string') out.set(j['instanceId'], str(j['desc']));
  }
  return out;
}

/* ===================================================== frame building === */

interface BuildInput {
  timeline: Keystroke[];
  events: GameEvent[];
  marks: (ClockMark & { elapsedMs: number })[];
  /** Fallback for a recording without a keystroke timeline. */
  storySegments: Segment[];
  panelStarts: number[];
  jokerDefs: Map<string, string>;
  authors: AuthorSeat[];
  config: SessionConfig;
  kind: RoundKind;
  fullText: string;
  roundMs: number;
  startedAtEpoch: number;
  endReason: EndReason | null;
}

function buildFrames(input: BuildInput): { frames: Frame[]; markers: PlaybackMarker[] } {
  const seats = input.authors.length;
  const frames: Frame[] = [];
  const markers: PlaybackMarker[] = [];
  const name = (i: number) => input.authors[i]?.name ?? `Autor:in ${i + 1}`;

  // Without a keystroke timeline there is nothing to type back in, so the
  // text stands finished from the first frame — story.json alone still
  // replays the clocks, the jokers and the margin notes over a story that is
  // simply already there. The loader says as much when it loads one.
  const staticText = input.timeline.length === 0;

  // Mutable working state; arrays are replaced (never mutated in place) so
  // the frames that still point at the old ones stay correct.
  let atMs = 0;
  let textLen = staticText ? input.fullText.length : 0;
  let activeAuthor = firstAuthor(input);
  let remainingMs = Array.from({ length: seats }, () => input.config.startMs);
  let frozen = Array.from({ length: seats }, () => false);
  let confirmedDone = Array.from({ length: seats }, () => false);
  let phase: RoundPhase = 'running';
  let panelStarts = staticText ? [...input.panelStarts] : [0];
  let activeJokers: ActiveJoker[] = [];
  let endedJokers: ActiveJoker[] = [];
  let marks: ClockMark[] = [];
  let segments: Segment[] = staticText ? input.storySegments : [];
  let endReason: EndReason | null = null;
  let settledAt = 0;

  const ticking = (): number | null =>
    phase === 'running' && !frozen[activeAuthor] && (remainingMs[activeAuthor] ?? 0) > 0 ? activeAuthor : null;

  /**
   * Run the active clock forward to `now` — the engine's settle() plus its
   * zero-crossing rule (A2). A clock running out leaves no keystroke and no
   * trace in keystrokes.json, so the replay has to derive it: the exhausted
   * seat freezes, the other one takes over, and when nobody has time left
   * the round is over. A frame is emitted at the crossing itself, which is
   * what makes the hand-over land on the right second on the beamer.
   */
  const settle = (now: number) => {
    for (;;) {
      const run = ticking();
      if (run === null) break;
      const left = remainingMs[run] ?? 0;
      if (settledAt + left > now) {
        remainingMs = remainingMs.slice();
        remainingMs[run] = left - (now - settledAt);
        break;
      }
      const crossing = settledAt + left;
      remainingMs = remainingMs.slice();
      remainingMs[run] = 0;
      frozen = frozen.slice();
      frozen[run] = true;
      settledAt = crossing;
      atMs = crossing;
      markers.push({ atMs: PREROLL_MS + crossing, kind: 'frozen', label: `${name(run)} 0:00` });
      const next = nextWithTime(run, frozen, remainingMs, seats);
      if (next === null) {
        phase = 'ended';
        endReason = 'time';
        emit();
        break;
      }
      if (input.kind === 'comic' && panelStarts.length < COMIC_PANELS) panelStarts = [...panelStarts, textLen];
      activeAuthor = next;
      emit();
    }
    settledAt = now;
    atMs = now;
  };

  const emit = () => {
    const frame: Frame = {
      atMs,
      textLen,
      activeAuthor,
      remainingMs,
      ticking: ticking(),
      frozen,
      confirmedDone,
      phase,
      panelStarts,
      activeJokers,
      endedJokers,
      marks,
      segments,
      endReason,
    };
    const last = frames[frames.length - 1];
    if (last && last.atMs === atMs) frames[frames.length - 1] = frame;
    else frames.push(frame);
  };

  emit(); // t = 0: the round starts
  markers.push({ atMs: PREROLL_MS, kind: 'start', label: `${name(activeAuthor)} beginnt` });

  /** Both logs share one seq counter, so they interleave losslessly (F-35). */
  const steps = mergeSteps(input.timeline, input.events, input.marks);

  for (const step of steps) {
    settle(step.atMs);

    if (step.stroke) {
      const k = step.stroke;
      // The clocks the server recorded win over our interpolation.
      if (k.clocksMs.length === seats) {
        remainingMs = k.clocksMs.slice();
        const newlyFrozen = remainingMs.map((ms, i) => frozen[i] === true || ms <= 0);
        if (newlyFrozen.some((f, i) => f !== frozen[i])) frozen = newlyFrozen;
      }
      if (k.kind === 'char') {
        // Only the seat at the keyboard can land a character — so the
        // stroke itself tells us whose turn it is, even without story.json.
        activeAuthor = k.author;
        if (k.char !== null) {
          const lastSeg = segments[segments.length - 1];
          if (!lastSeg || lastSeg.author !== k.author) {
            segments = [...segments, { author: k.author, start: textLen }];
          }
          textLen += k.char.length;
        }
      } else if (k.kind === 'turn_pass') {
        const next = nextWithTime(k.author, frozen, remainingMs, seats);
        if (next !== null) {
          if (input.kind === 'comic' && panelStarts.length < COMIC_PANELS) {
            panelStarts = [...panelStarts, textLen];
          }
          activeAuthor = next;
          markers.push({ atMs: PREROLL_MS + step.atMs, kind: 'pass', label: `→ ${name(next)}` });
        }
      }
      // `blocked` strokes are invisible on stage — they only carry clocks.
    }

    if (step.event) {
      const ev = step.event;
      const d = ev.data;
      switch (ev.type) {
        case 'round_start':
          if (typeof d['firstAuthor'] === 'number') activeAuthor = d['firstAuthor'];
          break;
        case 'pause':
          phase = 'paused';
          activeJokers = activeJokers.map((j) =>
            j.endsAt === null
              ? j
              : { ...j, remainingMsAtPause: Math.max(0, j.endsAt - (input.startedAtEpoch + step.atMs)), endsAt: null },
          );
          markers.push({ atMs: PREROLL_MS + step.atMs, kind: 'pause', label: 'Pause' });
          break;
        case 'resume':
          phase = 'running';
          activeJokers = activeJokers.map((j) =>
            j.remainingMsAtPause === null
              ? j
              : { ...j, endsAt: input.startedAtEpoch + step.atMs + j.remainingMsAtPause, remainingMsAtPause: null },
          );
          break;
        case 'author_frozen': {
          const a = num(d['author']);
          if (frozen[a] !== true) {
            frozen = frozen.slice();
            frozen[a] = true;
            markers.push({ atMs: PREROLL_MS + step.atMs, kind: 'frozen', label: `${name(a)} 0:00` });
          }
          break;
        }
        case 'turn_pass': {
          // Only the timeout pass has no keystroke of its own.
          if (d['cause'] === 'timeout' && typeof d['to'] === 'number') {
            if (input.kind === 'comic' && panelStarts.length < COMIC_PANELS) panelStarts = [...panelStarts, textLen];
            activeAuthor = d['to'];
            markers.push({ atMs: PREROLL_MS + step.atMs, kind: 'pass', label: `Zeit → ${name(activeAuthor)}` });
          }
          break;
        }
        case 'turn_pass_undone': {
          if (typeof d['restoredActive'] === 'number') activeAuthor = d['restoredActive'];
          if (Array.isArray(d['restoredRemainingMs'])) remainingMs = (d['restoredRemainingMs'] as number[]).slice();
          break;
        }
        case 'joker_start': {
          const instanceId = str(d['instanceId'], `j-${ev.seq}`);
          const durationMs = typeof d['durationMs'] === 'number' ? d['durationMs'] : null;
          const joker: ActiveJoker = {
            instanceId,
            defId: str(d['defId']),
            name: str(d['name'], 'Joker'),
            desc: input.jokerDefs.get(instanceId) ?? '',
            target: (d['target'] === 'global' ? 'global' : num(d['target'])) as JokerTarget,
            startedAt: input.startedAtEpoch + step.atMs,
            endsAt: durationMs === null ? null : input.startedAtEpoch + step.atMs + durationMs,
            remainingMsAtPause: null,
            startTextPos: num(d['textPos'], textLen),
            endTextPos: null,
          };
          activeJokers = [...activeJokers, joker];
          markers.push({ atMs: PREROLL_MS + step.atMs, kind: 'joker', label: joker.name });
          break;
        }
        case 'joker_end': {
          const id = str(d['instanceId']);
          const j = activeJokers.find((x) => x.instanceId === id);
          if (j) {
            activeJokers = activeJokers.filter((x) => x.instanceId !== id);
            endedJokers = [...endedJokers, { ...j, endTextPos: num(d['textPos'], textLen) }];
            markers.push({ atMs: PREROLL_MS + step.atMs, kind: 'jokerEnd', label: `${j.name} endet` });
          }
          break;
        }
        case 'penalty':
        case 'clock_adjust': {
          const a = num(d['author']);
          const delta = ev.type === 'penalty' ? -Math.abs(num(d['ms'])) : num(d['deltaMs']);
          remainingMs = remainingMs.slice();
          remainingMs[a] = Math.max(0, (remainingMs[a] ?? 0) + delta);
          if ((remainingMs[a] ?? 0) > 0 && frozen[a]) {
            frozen = frozen.slice();
            frozen[a] = false;
          }
          markers.push({
            atMs: PREROLL_MS + step.atMs,
            kind: delta < 0 ? 'penalty' : 'bonus',
            label: `${delta < 0 ? '−' : '+'}${Math.round(Math.abs(delta) / 1000)} s ${name(a)}`,
          });
          break;
        }
        case 'completion_confirmed':
        case 'completion_retracted': {
          const a = num(d['author']);
          confirmedDone = confirmedDone.slice();
          confirmedDone[a] = ev.type === 'completion_confirmed';
          break;
        }
        case 'round_end':
          phase = 'ended';
          endReason = parseEndReason(d['reason']) ?? input.endReason;
          break;
      }
    }

    if (step.mark) {
      marks = [...marks, stripElapsed(step.mark)];
    }

    emit();
  }

  // The round always ends, even when story.json is missing: the export's
  // endedAt is the last word.
  if (phase !== 'ended') {
    settle(input.roundMs);
    phase = 'ended';
    endReason = input.endReason;
    // Jokers still running when the whistle blows are closed by the engine.
    if (activeJokers.length > 0) {
      endedJokers = [...endedJokers, ...activeJokers.map((j) => ({ ...j, endTextPos: textLen }))];
      activeJokers = [];
    }
    emit();
  }
  markers.push({ atMs: PREROLL_MS + input.roundMs, kind: 'end', label: 'Ende' });

  markers.sort((a, b) => a.atMs - b.atMs);
  return { frames, markers };
}

function stripElapsed(m: ClockMark & { elapsedMs: number }): ClockMark {
  return { textPos: m.textPos, author: m.author, deltaMs: m.deltaMs, reason: m.reason, t: m.t };
}

function firstAuthor(input: BuildInput): number {
  const start = input.events.find((e) => e.type === 'round_start');
  if (start && typeof start.data['firstAuthor'] === 'number') return start.data['firstAuthor'];
  const firstChar = input.timeline.find((k) => k.kind === 'char');
  return firstChar?.author ?? 0;
}

function nextWithTime(from: number, frozen: boolean[], remainingMs: number[], seats: number): number | null {
  for (let step = 1; step < seats; step++) {
    const i = (from + step) % seats;
    if (!frozen[i] && (remainingMs[i] ?? 0) > 0) return i;
  }
  return null;
}

interface Step {
  atMs: number;
  stroke?: Keystroke;
  event?: GameEvent;
  mark?: ClockMark & { elapsedMs: number };
}

/**
 * Interleave the keystroke log, the event log and the clock marks on the one
 * time base they share. Marks ride along with their `penalty`/`clock_adjust`
 * event when there is one, and stand on their own otherwise.
 */
function mergeSteps(
  timeline: Keystroke[],
  events: GameEvent[],
  marks: (ClockMark & { elapsedMs: number })[],
): Step[] {
  const steps: Step[] = [];
  for (const k of timeline) steps.push({ atMs: k.elapsedMs, stroke: k });
  for (const e of events) steps.push({ atMs: e.elapsedMs, event: e });
  for (const m of marks) steps.push({ atMs: m.elapsedMs, mark: m });
  steps.sort((a, b) => a.atMs - b.atMs || seqOf(a) - seqOf(b));
  return steps;
}

function seqOf(s: Step): number {
  return s.stroke?.seq ?? s.event?.seq ?? 0;
}

/* ========================================================= playback ==== */

/** Round time (0 … roundMs) for a position on the playback timeline. */
export function roundElapsed(rec: PlaybackRecording, playheadMs: number): number {
  return Math.min(Math.max(0, playheadMs - PREROLL_MS), rec.roundMs);
}

function frameAt(frames: Frame[], t: number): Frame {
  let lo = 0;
  let hi = frames.length - 1;
  let found = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid]!.atMs <= t) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return frames[found]!;
}

/**
 * The state of the show at `playheadMs` — the exact shape the hub
 * broadcasts, so every view renders it without knowing it is a replay.
 * Below `PREROLL_MS` the round sits on its intro slide, the way it does on
 * the evening before the moderator says "Jetzt starten".
 */
export function stateAt(rec: PlaybackRecording, playheadMs: number): PublicState {
  const t = roundElapsed(rec, playheadMs);
  const intro = playheadMs < PREROLL_MS;
  const f = frameAt(rec.frames, t);
  const dt = t - f.atMs;

  const remainingMs = f.remainingMs.map((ms, i) => (i === f.ticking ? Math.max(0, ms - dt) : ms));
  const phase: RoundPhase = intro ? 'intro' : f.phase;
  const text = intro ? '' : rec.fullText.slice(0, f.textLen);

  return {
    pin: rec.pin,
    config: rec.config,
    display: rec.display,
    jokerDeck: [],
    comics: rec.comics,
    authors: rec.authors,
    maxAuthors: MAX_AUTHORS,
    plannedTotal: rec.config.roundsPlanned,
    round: {
      number: rec.roundNumber,
      kind: rec.kind,
      comicId: rec.comicId,
      panelStarts: intro ? [0] : f.panelStarts,
      prompt: rec.prompt,
      phase,
      activeAuthor: f.activeAuthor,
      remainingMs: intro ? rec.authors.map(() => rec.config.startMs) : remainingMs,
      frozen: intro ? rec.authors.map(() => false) : f.frozen,
      confirmedDone: intro ? rec.authors.map(() => false) : f.confirmedDone,
      text,
      segments: intro ? [] : f.segments,
      activeJokers: intro ? [] : f.activeJokers,
      endedJokers: intro ? [] : f.endedJokers,
      marks: intro ? [] : f.marks,
      startedAt: rec.startedAtEpoch,
      endedAt: phase === 'ended' ? rec.startedAtEpoch + rec.roundMs : null,
      endReason: phase === 'ended' ? f.endReason ?? rec.endReason : null,
      recentEvents: [],
      keystrokeCount: rec.keystrokeCount,
      undoAvailable: false,
    },
    pastRounds: [],
  };
}

/**
 * The epoch instant the replay is currently standing on. Feeding it to the
 * views as `clockOffset` makes every wall-clock deadline in the recording —
 * the joker countdowns — tick in replay time instead of today's time.
 */
export function virtualClockOffset(rec: PlaybackRecording, playheadMs: number, now: number): number {
  return rec.startedAtEpoch + roundElapsed(rec, playheadMs) - now;
}

/** Next/previous marker for the ⏭ / ⏮ transport buttons. */
export function seekMarker(rec: PlaybackRecording, playheadMs: number, dir: 1 | -1): number {
  const eps = 250;
  if (dir === 1) {
    const next = rec.markers.find((m) => m.atMs > playheadMs + eps);
    return next ? next.atMs : rec.totalMs;
  }
  const prev = [...rec.markers].reverse().find((m) => m.atMs < playheadMs - eps);
  return prev ? prev.atMs : 0;
}

/**
 * Stretches of the round where nothing at all happens, in **round time** —
 * the raw material for both "Leerlauf überspringen" in the transport and the
 * time-lapse of a social clip (see clip.ts).
 */
export function roundGaps(rec: PlaybackRecording, thresholdMs: number): { from: number; to: number }[] {
  const gaps: { from: number; to: number }[] = [];
  let previous = 0;
  for (const frame of rec.frames) {
    if (frame.atMs - previous > thresholdMs) gaps.push({ from: previous, to: frame.atMs });
    previous = frame.atMs;
  }
  if (rec.roundMs - previous > thresholdMs) gaps.push({ from: previous, to: rec.roundMs });
  return gaps;
}

/**
 * Idle stretches, for "Leerlauf überspringen": gaps where nothing at all
 * happens for longer than `thresholdMs` are jumped over, so a fourteen-minute
 * round with two long silences still plays at its own pace but does not make
 * the audience wait through them. Half the threshold is left standing at each
 * end, so the jump never swallows the beat around it.
 */
export function idleGaps(rec: PlaybackRecording, thresholdMs = 4_000): { from: number; to: number }[] {
  return roundGaps(rec, thresholdMs).map((g) => ({
    from: PREROLL_MS + g.from + thresholdMs / 2,
    to: PREROLL_MS + g.to - thresholdMs / 2,
  }));
}

/** The finished round as a state — what the outro card of a clip reports. */
export function finalState(rec: PlaybackRecording): PublicState {
  return stateAt(rec, rec.totalMs);
}
